<template>
  <div class="doc-collection">
    <div class="doc-collection__header">
      <p class="doc-collection__title">{{ title }}</p>
      <div class="doc-collection__toggle" :class="{ 'doc-collection__toggle--boxed': boxedToggle }">
        <vs-switch v-model="enabledModel" />
        <span class="doc-collection__toggle-label">{{ enabled ? 'Yes' : 'No' }}</span>
      </div>
    </div>

    <template v-if="enabled">
      <div class="doc-collection__grid">
        <div
          v-for="(doc, dIdx) in documents"
          :key="docKey(doc, dIdx)"
          class="doc-card"
        >
          <div class="doc-card__top">
            <div class="doc-card__body">
              <div v-if="doc._editing">
                <div class="doc-card__field">
                  <label>Document Name</label>
                  <vs-input v-model="doc.name" class="w-full" @input="markDirty(doc)" />
                </div>
                <div class="doc-card__field">
                  <label>Required?</label>
                  <div class="doc-card__radios">
                    <vs-radio
                      v-model="doc.mandatory"
                      :vs-value="true"
                      :vs-name="radioName(dIdx)"
                      @change="markDirty(doc)"
                    >Required</vs-radio>
                    <vs-radio
                      v-model="doc.mandatory"
                      :vs-value="false"
                      :vs-name="radioName(dIdx)"
                      @change="markDirty(doc)"
                    >Optional</vs-radio>
                  </div>
                </div>
                <div class="doc-card__field">
                  <label>Hint</label>
                  <vs-textarea v-model="doc.hint" height="90px" @input="markDirty(doc)" />
                </div>
                <div class="doc-card__edit-actions">
                  <vs-button
                    type="relief"
                    size="small"
                    class="vs-con-loading__container"
                    :id="elId('save', dIdx)"
                    :disabled="!doc._dirty"
                    @click.prevent="saveDocument(dIdx)"
                  >Save</vs-button>
                  <vs-button
                    type="border"
                    size="small"
                    class="vs-con-loading__container"
                    :id="elId('cancel', dIdx)"
                    @click.prevent="cancelEdit(dIdx)"
                  >Cancel</vs-button>
                  <span v-if="doc._dirty" class="doc-card__unsaved">Unsaved changes</span>
                </div>
              </div>
              <div v-else>
                <h4 class="doc-card__name">{{ doc.name }}</h4>
                <p class="doc-card__status">{{ doc.mandatory ? 'Required' : 'Optional' }}</p>
                <p class="doc-card__hint">{{ doc.hint }}</p>
              </div>
            </div>

            <div class="doc-card__actions">
              <a
                v-if="!doc._editing"
                class="doc-card__action vs-con-loading__container"
                :id="elId('edit', dIdx)"
                @click.prevent="editDocument(dIdx)"
              >
                <feather-icon icon="EditIcon" :svgClasses="['w-4', 'h-4']" />
                <span>Edit</span>
              </a>
              <a
                class="doc-card__action vs-con-loading__container"
                :id="elId('delete', dIdx)"
                @click.prevent="removeDocument(dIdx)"
              >
                <feather-icon icon="TrashIcon" :svgClasses="['w-4', 'h-4']" />
                <span>Delete</span>
              </a>
            </div>
          </div>
        </div>

        <div class="doc-collection__add-cell">
          <a
            class="doc-collection__add vs-con-loading__container"
            :id="elId('add')"
            @click.prevent="addDocument"
          >+ Add new document</a>
        </div>
      </div>

      <div class="doc-collection__summary">
        {{ requiredCount }} {{ requiredCount === 1 ? 'document' : 'documents' }} compulsory.
        {{ optionalCount }} {{ optionalCount === 1 ? 'document' : 'documents' }} optional.
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'DocumentCollectionSection',
  props: {
    enabled: {
      type: Boolean,
      default: false
    },
    documents: {
      type: Array,
      required: true
    },
    title: {
      type: String,
      default: 'Collect Documents?'
    },
    boxedToggle: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      uid: `dc-${Math.random().toString(36).slice(2, 8)}`
    }
  },
  computed: {
    enabledModel: {
      get() {
        return this.enabled
      },
      set(val) {
        this.$emit('update:enabled', val)
      }
    },
    requiredCount() {
      return this.documents.filter((d) => d.mandatory).length
    },
    optionalCount() {
      return this.documents.filter((d) => !d.mandatory).length
    }
  },
  watch: {
    documents: {
      immediate: true,
      handler(list) {
        if (!Array.isArray(list)) return
        list.forEach((doc) => this.ensureFlags(doc))
      }
    }
  },
  methods: {
    ensureFlags(doc) {
      if (doc._editing === undefined) this.$set(doc, '_editing', false)
      if (doc._dirty === undefined) this.$set(doc, '_dirty', false)
      if (doc._backup === undefined) this.$set(doc, '_backup', null)
      if (doc._isNew === undefined) this.$set(doc, '_isNew', false)
    },
    docKey(doc, index) {
      return `${this.uid}-${index}`
    },
    elId(action, index) {
      return index === undefined ? `${this.uid}-${action}` : `${this.uid}-${action}-${index}`
    },
    radioName(index) {
      return `${this.uid}-required-${index}`
    },
    loadingSelector(action, index) {
      return `#${this.elId(action, index)}`
    },
    markDirty(doc) {
      this.$set(doc, '_dirty', true)
    },
    async addDocument() {
      this.$vs.loading({ container: this.loadingSelector('add'), scale: 0.45 })
      await new Promise((resolve) => setTimeout(resolve, 200))
      this.documents.push({
        name: 'New Document',
        mandatory: true,
        hint: 'Upload both front and back side together',
        _editing: true,
        _dirty: true,
        _isNew: true,
        _backup: null
      })
      this.$vs.loading.close(`${this.loadingSelector('add')} > .con-vs-loading`)
    },
    removeDocument(index) {
      const doc = this.documents[index]
      if (!doc) return
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Confirm Delete',
        text: `Are you sure you want to delete "${doc.name}"? This action cannot be undone.`,
        accept: async () => {
          this.$vs.loading({ container: this.loadingSelector('delete', index), scale: 0.45 })
          await new Promise((resolve) => setTimeout(resolve, 200))
          this.documents.splice(index, 1)
          this.$vs.loading.close(`${this.loadingSelector('delete', index)} > .con-vs-loading`)
          this.$vs.notify({
            title: 'Success',
            text: 'Document deleted successfully',
            color: 'success',
            position: 'top-center'
          })
        }
      })
    },
    async editDocument(index) {
      const doc = this.documents[index]
      if (!doc || doc._editing) return
      this.$vs.loading({ container: this.loadingSelector('edit', index), scale: 0.45 })
      await new Promise((resolve) => setTimeout(resolve, 150))
      this.$set(doc, '_backup', {
        name: doc.name,
        hint: doc.hint,
        mandatory: doc.mandatory
      })
      this.$set(doc, '_editing', true)
      this.$set(doc, '_dirty', false)
      this.$vs.loading.close(`${this.loadingSelector('edit', index)} > .con-vs-loading`)
    },
    async cancelEdit(index) {
      const doc = this.documents[index]
      if (!doc) return
      this.$vs.loading({ container: this.loadingSelector('cancel', index), scale: 0.45 })
      await new Promise((resolve) => setTimeout(resolve, 150))
      if (doc._isNew) {
        this.documents.splice(index, 1)
        this.$vs.loading.close(`${this.loadingSelector('cancel', index)} > .con-vs-loading`)
        return
      }
      if (doc._backup) {
        doc.name = doc._backup.name
        doc.hint = doc._backup.hint
        doc.mandatory = doc._backup.mandatory
      }
      this.$set(doc, '_editing', false)
      this.$set(doc, '_dirty', false)
      this.$set(doc, '_backup', null)
      this.$vs.loading.close(`${this.loadingSelector('cancel', index)} > .con-vs-loading`)
    },
    async saveDocument(index) {
      const doc = this.documents[index]
      if (!doc) return
      this.$vs.loading({ container: this.loadingSelector('save', index), scale: 0.45 })
      await new Promise((resolve) => setTimeout(resolve, 200))
      if (!doc.name || !String(doc.name).trim()) {
        this.$vs.notify({
          title: 'Error',
          text: 'Document name is required',
          color: 'danger',
          position: 'top-center'
        })
        this.$vs.loading.close(`${this.loadingSelector('save', index)} > .con-vs-loading`)
        return
      }
      this.$set(doc, 'name', String(doc.name).trim())
      this.$set(doc, '_editing', false)
      this.$set(doc, '_dirty', false)
      this.$set(doc, '_backup', null)
      this.$set(doc, '_isNew', false)
      this.$vs.loading.close(`${this.loadingSelector('save', index)} > .con-vs-loading`)
      this.$vs.notify({
        title: 'Success',
        text: 'Document saved successfully',
        color: 'success',
        position: 'top-center'
      })
    }
  }
}
</script>

<style scoped>
.doc-collection {
  width: 100%;
}

.doc-collection__header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 20px;
  margin-bottom: 16px;
}

.doc-collection__title {
  margin: 0;
  font-size: 26px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.25;
}

.doc-collection__toggle {
  display: inline-flex;
  align-items: center;
  gap: 12px;
}

.doc-collection__toggle--boxed {
  background: var(--card-bg);
  padding: 5px 20px;
  border-radius: 10px;
}

.doc-collection__toggle-label {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
}

.doc-collection__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  width: 100%;
  margin-bottom: 16px;
}

@media (min-width: 768px) {
  .doc-collection__grid {
    grid-template-columns: 1fr 1fr;
  }
}

.doc-card {
  background: var(--card-bg);
  border: 1px solid var(--divider);
  border-radius: 8px;
  padding: 16px 18px;
  min-height: 112px;
  box-sizing: border-box;
}

.doc-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.doc-card__body {
  min-width: 0;
  flex: 1;
}

.doc-card__name {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.3;
}

.doc-card__status {
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 700;
  color: var(--text-primary);
}

.doc-card__hint {
  margin: 0;
  font-size: 13px;
  font-weight: 400;
  color: var(--text-secondary);
  line-height: 1.4;
}

.doc-card__actions {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}

.doc-card__action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--blue-text);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
}

.doc-card__action:hover {
  opacity: 0.75;
}

.doc-card__field {
  margin-bottom: 10px;
}

.doc-card__field label {
  display: block;
  margin-bottom: 4px;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
}

.doc-card__radios {
  display: flex;
  align-items: center;
  gap: 16px;
}

.doc-card__edit-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
}

.doc-card__unsaved {
  font-size: 13px;
  color: #d97706;
}

.doc-collection__add-cell {
  display: flex;
  align-items: center;
  min-height: 112px;
  padding-left: 4px;
}

.doc-collection__add {
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
}

.doc-collection__add:hover {
  text-decoration: underline;
}

.doc-collection__summary {
  display: inline-block;
  background: #e7f8ea;
  color: #14532d;
  font-size: 15px;
  font-weight: 500;
  border-radius: 6px;
  padding: 8px 14px;
}

@media (max-width: 640px) {
  .doc-card__top {
    flex-direction: column;
  }

  .doc-card__actions {
    width: 100%;
    justify-content: flex-end;
  }

  .doc-collection__add-cell {
    min-height: auto;
    padding: 8px 0;
  }
}
</style>
